<template>
  <div class="notes-app">
    <!-- Main Container -->
    <div class="notes-container">
      <!-- Header Section -->
      <header class="notes-header">
        <div class="header-content">
          <div class="title-section">
            <div class="logo-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h1>Notes</h1>
            <span class="note-count">{{ notes.length }} notes</span>
          </div>
          <div class="header-actions">
            <SearchBar 
              v-model="searchQuery" 
              placeholder="Search notes..." 
              label="Search" 
            />
            <button class="btn-add-note" @click="showNewNoteModal = true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 4v16m8-8H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>New Note</span>
            </button>
          </div>
        </div>
      </header>



      <!-- Notes Grid -->
      <div class="notes-grid">
        <transition-group name="note-card">
          <div 
            v-for="note in filteredNotes" 
            :key="note.id" 
            class="note-card"
            :class="{ pinned: note.pinned }"
            @click="openNote(note)"
          >
            <!-- Pin Badge -->
            <div class="pin-badge" v-if="note.pinned">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
              </svg>
            </div>



            <!-- Note Content -->
            <div class="note-content">
              <h3 class="note-title">{{ note.title || 'Untitled' }}</h3>
              <p class="note-preview">{{ note.content }}</p>
            </div>

            <!-- Note Footer -->
            <div class="note-footer">
              <span class="note-date">{{ formatDate(note.updatedAt) }}</span>
              <div class="note-actions">
                <button 
                  class="action-btn pin-btn" 
                  @click.stop="togglePin(note)"
                  :class="{ active: note.pinned }"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button class="action-btn delete-btn" @click.stop="confirmDelete(note)">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </transition-group>

        <!-- Empty State -->
        <div class="empty-state" v-if="filteredNotes.length === 0">
          <div class="empty-illustration">
            <svg viewBox="0 0 100 100" fill="none">
              <rect x="25" y="20" width="50" height="60" rx="4" stroke="currentColor" stroke-width="2" stroke-dasharray="4 4"/>
              <line x1="35" y1="35" x2="65" y2="35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <line x1="35" y1="45" x2="55" y2="45" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <line x1="35" y1="55" x2="60" y2="55" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <h3>No notes yet</h3>
          <p>Create your first note to get started</p>
          <button class="btn-create-first" @click="showNewNoteModal = true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 4v16m8-8H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Create Note
          </button>
        </div>
      </div>
    </div>

    <!-- Note Modal (Create/Edit) -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showNewNoteModal || selectedNote" @click.self="closeModal">
        <div class="note-modal">
          <div class="modal-header">
            <input 
              type="text" 
              class="modal-title-input" 
              placeholder="Note title..."
              v-model="editingNote.title"
              ref="titleInput"
            />
            <div class="modal-actions">

              <button class="modal-btn close-btn" @click="closeModal">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="modal-body">
            <textarea 
              class="note-textarea" 
              placeholder="Start writing your note..."
              v-model="editingNote.content"
            ></textarea>
          </div>

          <div class="modal-footer">
            <div class="footer-info">
              <span v-if="editingNote.updatedAt">Last edited {{ formatDate(editingNote.updatedAt) }}</span>
              <span v-else>New note</span>
            </div>
            <div class="footer-actions">
              <button class="btn-cancel" @click="closeModal">Cancel</button>
              <button class="btn-save" @click="saveNote">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Save Note
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast Notification -->
    <transition name="toast-slide">
      <div class="toast-notification" v-if="toastMessage" :class="toastType">
        <svg v-if="toastType === 'success'" viewBox="0 0 24 24" fill="none">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none">
          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>{{ toastMessage }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import SearchBar from "@/components/SearchBar/SearchBar.vue";
import { useConfirmation } from "@/components/Toast/useConfirmation";
import { useToast } from "@/components/Toast/useToast";

export default {
  name: 'NoteTaker',
  components: {
    SearchBar
  },
  props: {
    applicationId: {
      type: [String, Number],
      required: true
    }
  },
  setup() {
    const { confirmDelete } = useConfirmation();
    const { showSuccess, showError } = useToast();
    
    return {
      showDeleteConfirmation: confirmDelete,
      showSuccessToast: showSuccess,
      showErrorToast: showError
    };
  },
  data() {
    return {
      searchQuery: '',
      showNewNoteModal: false,
      selectedNote: null,
      toastMessage: '',
      toastType: 'success',
      isLoading: false,
      editingNote: {
        id: null,
        title: '',
        content: '',
        pinned: false,
        created_at: null,
        updated_at: null
      },
      notes: []
    };
  },
  computed: {
    filteredNotes() {
      let result = [...this.notes];
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(note => 
          (note.title && note.title.toLowerCase().includes(query)) || 
          (note.content && note.content.toLowerCase().includes(query))
        );
      }
      
      // Sort: pinned first, then by date
      result.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      
      return result;
    }
  },
  watch: {
    applicationId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchNotes();
        }
      }
    }
  },
  methods: {
    async fetchNotes() {
      if (!this.applicationId) return;
      
      this.isLoading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/verify_api/customer_management_api/get_application_notes_api/?application_id=${this.applicationId}`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch notes');
        
        const data = await response.json();
        this.notes = data;
      } catch (error) {
        console.error('Error fetching notes:', error);
        this.showToast('Failed to load notes', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    },
    

    
    openNote(note) {
      this.selectedNote = note;
      this.editingNote = { ...note };
      this.$nextTick(() => {
        if (this.$refs.titleInput) {
          this.$refs.titleInput.focus();
        }
      });
    },
    
    closeModal() {
      this.showNewNoteModal = false;
      this.selectedNote = null;
      this.editingNote = {
        id: null,
        title: '',
        content: '',
        pinned: false,
        created_at: null,
        updated_at: null
      };
    },
    
    async saveNote() {
      if (!this.editingNote.title?.trim() && !this.editingNote.content?.trim()) {
        this.showToast('Please add a title or content', 'error');
        return;
      }
      
      const token = localStorage.getItem('token');
      const isUpdate = !!this.editingNote.id;
      const url = isUpdate 
        ? `${API_BASE_URL}/verify_api/customer_management_api/update_application_note_api/`
        : `${API_BASE_URL}/verify_api/customer_management_api/save_application_note_api/`;
        
      // For FBV, method is usually POST for both create and update
      const method = 'POST';
      
      const payload = {
        ...this.editingNote,
        customer_application: this.applicationId
      };
      
      try {
        const response = await fetch(url, {
          method: method,
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) throw new Error('Failed to save note');
        
        const savedNote = await response.json();
        
        if (isUpdate) {
          const index = this.notes.findIndex(n => n.id === savedNote.id);
          if (index !== -1) {
            this.notes.splice(index, 1, savedNote);
          }
          this.showToast('Note updated successfully', 'success');
        } else {
          this.notes.unshift(savedNote);
          this.showToast('Note created successfully', 'success');
        }
        
        this.closeModal();
      } catch (error) {
        console.error('Error saving note:', error);
        this.showToast('Failed to save note', 'error');
      }
    },
    
    async togglePin(note) {
      const token = localStorage.getItem('token');
      try {
        const url = `${API_BASE_URL}/verify_api/customer_management_api/update_application_note_api/`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            id: note.id,
            pinned: !note.pinned 
          })
        });
        
        if (!response.ok) throw new Error('Failed to update pin status');
        
        const updatedNote = await response.json();
        const index = this.notes.findIndex(n => n.id === updatedNote.id);
        if (index !== -1) {
          this.notes.splice(index, 1, updatedNote);
        }
        
        this.showToast(updatedNote.pinned ? 'Note pinned' : 'Note unpinned', 'success');
      } catch (error) {
        console.error('Error toggling pin:', error);
        this.showToast('Failed to update note', 'error');
      }
    },
    
    confirmDelete(note) {
      const noteTitle = note.title || 'Untitled';
      this.showDeleteConfirmation(`"${noteTitle}"`)
        .then(() => {
          this.deleteNote(note);
        })
        .catch(() => {
          // User cancelled - do nothing
        });
    },
    
    async deleteNote(note) {
      if (!note) return;
      
      const token = localStorage.getItem('token');
      try {
        const url = `${API_BASE_URL}/verify_api/customer_management_api/delete_application_note_api/?note_id=${note.id}`;
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Token ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to delete note');
        
        const index = this.notes.findIndex(n => n.id === note.id);
        if (index !== -1) {
          this.notes.splice(index, 1);
        }
        this.showToast('Note deleted', 'success');
      } catch (error) {
        console.error('Error deleting note:', error);
        this.showToast('Failed to delete note', 'error');
      }
    },
    
    showToast(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;
      setTimeout(() => {
        this.toastMessage = '';
      }, 3000);
    }
  }
};
</script>

<style scoped>
/* ===== Base Styles - Light Theme Only ===== */
.notes-app {
  min-height: 100vh;
  background: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #000000;
  position: relative;
}

/* ===== Container ===== */
.notes-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
}

/* ===== Header ===== */
.notes-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-icon {
  width: 30px;
  height: 30px;
  background: var(--primary-color, #59FF74);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-icon svg {
  width: 20px;
  height: 20px;
  color: #ffffff;
}

.title-section h1 {
  font-size: 28px;
  font-weight: 700;
  color: #000000;
  margin: 0;
}

.note-count {
  background: #f5f5f5;
  color: #666666;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 100px;
  border: 1px solid #e0e0e0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}



/* ===== Add Note Button ===== */
.btn-add-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.4rem 0.6rem;
  background: #f8f9fa;
  border: 1.5px solid #dee1e5;
  border-radius: 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  height: fit-content;
}

.btn-add-note svg {
  width: 18px;
  height: 18px;
}

.btn-add-note:hover {
  background: #ffffff;
  color: #495057;
  border-color: #adb5bd;
}



/* ===== Notes Grid ===== */
.notes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

/* ===== Note Card ===== */
.note-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 180px;
}

.note-card:hover {
  border-color: #4c4c4c;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.note-card.pinned {
  border-color: var(--primary-color, #59FF74);
}



/* Pin Badge */
.pin-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: var(--primary-color, #59FF74);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.pin-badge svg {
  width: 14px;
  height: 14px;
}

/* Note Content */
.note-content {
  flex: 1;
  margin-bottom: 16px;
}

.note-title {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.note-preview {
  font-size: 14px;
  color: #666666;
  margin: 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
}

/* Note Footer */
.note-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.note-date {
  font-size: 12px;
  color: #999999;
}

.note-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666666;
  transition: all 0.2s ease;
}

.action-btn svg {
  width: 16px;
  height: 16px;
}

.action-btn:hover {
  border-color: #000000;
  color: #000000;
}

.action-btn.pin-btn.active {
  background: var(--primary-color, #59FF74);
  border-color: var(--primary-color, #59FF74);
  color: #ffffff;
}

.action-btn.delete-btn:hover {
  border-color: #dc3545;
  color: #dc3545;
}

/* ===== Empty State ===== */
.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
}

.empty-illustration {
  width: 120px;
  height: 120px;
  margin: 0 auto 24px;
  color: #e0e0e0;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: #000000;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: #666666;
  margin: 0 0 24px 0;
}

.btn-create-first {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: var(--primary-color, #59FF74);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-create-first svg {
  width: 20px;
  height: 20px;
}

.btn-create-first:hover {
  opacity: 0.9;
}

/* ===== Modal Overlay ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
}

/* ===== Note Modal ===== */
.note-modal {
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  gap: 16px;
}

.modal-title-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 20px;
  font-weight: 600;
  color: #000000;
}

.modal-title-input::placeholder {
  color: #999999;
}

.modal-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}



.modal-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666666;
  transition: all 0.2s ease;
}

.modal-btn svg {
  width: 18px;
  height: 18px;
}

.modal-btn:hover {
  background: #e0e0e0;
  color: #000000;
}

/* Modal Body */
.modal-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.note-textarea {
  width: 100%;
  min-height: 300px;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  font-size: 15px;
  line-height: 1.8;
  color: #000000;
  font-family: inherit;
}

.note-textarea::placeholder {
  color: #999999;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  background: #f9f9f9;
}

.footer-info span {
  font-size: 12px;
  color: #999999;
}

.footer-actions {
  display: flex;
  gap: 12px;
}

.btn-cancel {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #666666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover {
  background: #f5f5f5;
  border-color: #000000;
  color: #000000;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--primary-color, #59FF74);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save svg {
  width: 16px;
  height: 16px;
}

.btn-save:hover {
  opacity: 0.9;
}

/* ===== Toast Notification ===== */
.toast-notification {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: #000000;
  color: #ffffff;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  z-index: 3000;
}

.toast-notification svg {
  width: 20px;
  height: 20px;
}

.toast-notification.success {
  background: #000000;
}

.toast-notification.error {
  background: #dc3545;
}

/* ===== Animations ===== */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .note-modal,
.modal-fade-enter-active .delete-modal {
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from,
.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.note-card-enter-active,
.note-card-leave-active {
  transition: all 0.3s ease;
}

.note-card-enter-from,
.note-card-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .search-container {
    flex: 1;
  }
  
  .notes-grid {
    grid-template-columns: 1fr;
  }
  
  .note-modal {
    max-height: 100vh;
    border-radius: 0;
  }
}
</style>
